/*
  <bloom-fit-recommender> — interactive "Find my size" for the Bloom PDP.
  Reads usual size + height + feel, maps to a recommended size, then drives
  Dawn's REAL variant picker (checks the matching size radio and dispatches a
  bubbling change) so the recommendation actually applies to the buy box.
  Guidance is honest per research: true-to-size by default, no size-down nudges
  (sizing down is the top cause of roll-down), free returns as the safety net.
*/
class BloomFitRecommender extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-fit-toggle]');
    this.panel = this.querySelector('[data-fit-panel]');
    this.usual = this.querySelector('[data-fit-usual]');
    this.heightSel = this.querySelector('[data-fit-height]');
    this.feel = this.querySelector('[data-fit-feel]');
    this.goBtn = this.querySelector('[data-fit-go]');
    this.result = this.querySelector('[data-fit-result]');
    this.sizeOut = this.querySelector('[data-fit-size]');
    this.confOut = this.querySelector('[data-fit-conf]');
    this.tipOut = this.querySelector('[data-fit-tip]');

    this.scale = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    this.baseMap = { xs: 0, s: 1, m: 2, l: 3, xl: 4, xxl: 5, xxxl: 6 };
    this.host = this.closest('product-info') || document;

    if (this.toggle) this.toggle.addEventListener('click', () => this.toggleOpen());
    if (this.goBtn) this.goBtn.addEventListener('click', () => this.recommend());
  }

  toggleOpen() {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', String(!open));
    if (this.panel) this.panel.hidden = open;
  }

  recommend() {
    const base = this.usual ? this.baseMap[this.usual.value] : undefined;
    if (base === undefined) {
      if (this.usual) {
        this.usual.focus();
        this.usual.classList.remove('bloom-shake');
        // reflow to restart the animation
        void this.usual.offsetWidth;
        this.usual.classList.add('bloom-shake');
      }
      return;
    }

    const feel = this.feel ? this.feel.value : 'medium';
    const height = this.heightSel ? this.heightSel.value : '';

    // Only size UP for a lighter/roomier feel. Firm & medium stay true to size —
    // sizing down is never recommended (it's what makes shapewear roll).
    let target = base;
    if (feel === 'light') target += 1;
    target = Math.max(0, Math.min(this.scale.length - 1, target));

    const picked = this.selectSize(target);
    const label = picked ? picked.label : this.scale[target];
    const exact = picked ? picked.exact : false;

    if (this.sizeOut) this.sizeOut.textContent = label;
    if (this.confOut) this.confOut.textContent = this.confidence(exact, feel);
    if (this.tipOut) this.tipOut.textContent = this.tip(feel, height, exact, picked);
    if (this.result) this.result.hidden = false;
  }

  // Find the size pill group inside this product's real variant picker.
  sizeInputs() {
    const groups = this.host.querySelectorAll('.product-form__input--pill');
    for (const fs of groups) {
      const legend = fs.querySelector('.form__label');
      const name = legend ? legend.textContent.trim().toLowerCase() : '';
      if (name.indexOf('size') === 0) {
        return Array.from(fs.querySelectorAll('input[type="radio"]'));
      }
    }
    // Fallback: last pill group is conventionally the size selector.
    const all = this.host.querySelectorAll('.product-form__input--pill');
    if (all.length) return Array.from(all[all.length - 1].querySelectorAll('input[type="radio"]'));
    return [];
  }

  // Map a size label / value to an index on our scale (handles ranges like "S/M").
  labelToIndex(value) {
    const t = (value || '').trim().toUpperCase().replace(/\s+/g, '');
    const direct = this.scale.indexOf(t);
    if (direct !== -1) return direct;
    const first = t.split(/[\/\-–—]/)[0];
    return this.scale.indexOf(first);
  }

  // Check the closest available size to the target index; returns what was chosen.
  selectSize(targetIdx) {
    const inputs = this.sizeInputs().filter((i) => !i.disabled && !i.classList.contains('disabled'));
    if (!inputs.length) return null;

    // Split size pills carry the single size they show in data-bloom-size; their `value`
    // is still the whole range ("XS/S"), which would collapse both halves onto XS.
    const mapped = inputs
      .map((inp) => {
        const shown = inp.dataset.bloomSize || inp.value;
        return { inp, idx: this.labelToIndex(shown), label: shown.trim() };
      })
      .filter((o) => o.idx !== -1);
    if (!mapped.length) return null;

    let choice = mapped.find((o) => o.idx === targetIdx);
    const exact = Boolean(choice);
    if (!choice) {
      choice = mapped.reduce((best, o) =>
        Math.abs(o.idx - targetIdx) < Math.abs(best.idx - targetIdx) ? o : best
      );
    }

    this.check(choice.inp);
    return { label: choice.label, exact };
  }

  check(input) {
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Briefly highlight the applied pill so the user sees the buy box respond.
    const label = this.host.querySelector('label[for="' + input.id + '"]');
    if (label) {
      label.classList.remove('bloom-pill-flash');
      void label.offsetWidth;
      label.classList.add('bloom-pill-flash');
    }
  }

  confidence(exact, feel) {
    if (!exact) return 'Closest fit';
    return feel === 'medium' ? 'High confidence' : 'Great match';
  }

  tip(feel, height, exact, picked) {
    let base;
    if (feel === 'light') {
      base = 'Sized up one for a relaxed, breathe-easy feel with gentle smoothing.';
    } else if (feel === 'firm') {
      base = 'True to your size for firm, sculpting hold — we never suggest sizing down, since that is what makes shapewear roll.';
    } else {
      base = 'True to your usual size for balanced hold that stays put all day.';
    }
    if (!exact && picked) {
      base += ' We matched the closest size we stock.';
    }
    if (height === 'petite') {
      base += ' On a petite frame the torso may sit slightly long — free 30-day returns if the fit is not right.';
    } else if (height === 'tall' || height === 'xtall') {
      base += ' On a taller frame, size up if the torso feels short — exchanges are always free.';
    }
    return base;
  }
}

if (!customElements.get('bloom-fit-recommender')) {
  customElements.define('bloom-fit-recommender', BloomFitRecommender);
}
